// Shared types + resolver for municipal water-restriction schedules.
// A city's rule is stored once (the schedule library); a property's *effective*
// allowed days are derived from that rule, the property's water class
// (residential vs non-residential), and the property's street number.
//
// Keep this module free of React and of anything that reaches apiClient: it is
// exercised directly by waterRestrictions.test.ts under the node test
// environment. timeFormat is pure Intl, so it is safe to import here.

import { formatTimeOfDay } from './timeFormat';

export type DayAbbr = 'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat';

export const DAYS: DayAbbr[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const DAY_LABELS: Record<DayAbbr, string> = {
  Sun: 'Sunday',
  Mon: 'Monday',
  Tue: 'Tuesday',
  Wed: 'Wednesday',
  Thu: 'Thursday',
  Fri: 'Friday',
  Sat: 'Saturday',
};

export type AssignmentType = 'parity' | 'last_digit' | 'fixed' | 'none';

/** Watering customer class. HOA common-area irrigation is non_residential by policy. */
export type WaterClass = 'residential' | 'non_residential';

export const WATER_CLASS_LABELS: Record<WaterClass, string> = {
  residential: 'Residential',
  non_residential: 'Non-residential (commercial / HOA common area)',
};

/** The rule for a single customer class. */
export interface WaterRule {
  assignment_type: AssignmentType;
  odd_days?: string[];
  even_days?: string[];
  fixed_days?: string[];
  by_last_digit?: Record<string, string[]>;
  no_water_window?: { start: string; end: string } | null;
  seasonal?: { start: string; end: string } | null;
  max_days_per_week?: number | null;
  drip_exempt?: boolean;
}

/** A city's rule, split by customer class (either may be null = not defined). */
export interface WaterSchedule {
  residential?: WaterRule | null;
  non_residential?: WaterRule | null;
}

export interface WaterRestrictionSchedule {
  id: string;
  city: string;
  state: string;
  authority: string | null;
  authority_type: 'city' | 'county' | 'district' | 'unknown';
  schedule: WaterSchedule;
  source_url: string | null;
  last_verified: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CityScheduleEntry {
  city: string;
  property_count: number;
  schedule: WaterRestrictionSchedule | null;
}

/** First run of digits in an address ("2501 Eagle Parkway" → 2501). */
export function parseStreetNumber(address: string | null | undefined): number | null {
  if (!address) return null;
  const m = /\d+/.exec(address);
  return m ? Number(m[0]) : null;
}

/** The rule that applies to a property of `cls`, falling back to the other class. */
export function ruleForClass(
  schedule: WaterSchedule | null | undefined,
  cls: WaterClass,
): WaterRule | null {
  if (!schedule) return null;
  return schedule[cls] ?? schedule.non_residential ?? schedule.residential ?? null;
}

const norm = (arr?: string[]): DayAbbr[] =>
  DAYS.filter((d) => (arr ?? []).some((x) => x.toLowerCase().startsWith(d.toLowerCase().slice(0, 3))));

/**
 * The days a property is allowed to water under a single-class rule + street
 * number. Returns null when the rule can't be resolved (e.g. parity rule but no
 * street number) so callers can show "verify" rather than a wrong answer.
 */
export function resolveRuleDays(
  rule: WaterRule | null | undefined,
  streetNumber: number | null,
): DayAbbr[] | null {
  if (!rule) return null;
  switch (rule.assignment_type) {
    case 'fixed':
      return norm(rule.fixed_days);
    case 'none':
      return [];
    case 'parity': {
      if (streetNumber == null) return null;
      return norm(streetNumber % 2 === 0 ? rule.even_days : rule.odd_days);
    }
    case 'last_digit': {
      if (streetNumber == null) return null;
      return norm(rule.by_last_digit?.[String(streetNumber % 10)]);
    }
    default:
      return null;
  }
}

/** Convenience: resolve a property's allowed days from a city schedule + class + street number. */
export function resolveAllowedDays(
  schedule: WaterSchedule | null | undefined,
  cls: WaterClass,
  streetNumber: number | null,
): DayAbbr[] | null {
  return resolveRuleDays(ruleForClass(schedule, cls), streetNumber);
}

export function formatDays(days: DayAbbr[] | null): string {
  if (days == null) return '—';
  if (days.length === 0) return 'No day restriction';
  return days.map((d) => DAY_LABELS[d]).join(', ');
}

export { norm as normalizeDays };

// ---------------------------------------------------------------------------
// Property policy (response of GET /api/job-sites/:id/water-restriction)
// ---------------------------------------------------------------------------

export interface PropertyWaterPolicy {
  city: string | null;
  zip: string | null;
  state: string | null;
  property_class: WaterClass | null;
  override: WaterRule | null; // property-level rule override (rare); wins over city rule
  address: string | null;
  schedule: WaterRestrictionSchedule | null; // the city schedule row
}

/** The single rule that applies to this property: property override, else the city rule for its class. */
export function resolvePropertyRule(policy: PropertyWaterPolicy | null): WaterRule | null {
  if (!policy) return null;
  if (policy.override && typeof policy.override === 'object') return policy.override;
  return ruleForClass(policy.schedule?.schedule, policy.property_class ?? 'non_residential');
}

/** The allowed days for this property (or null if undeterminable, e.g. parity without a street number). */
export function resolvePropertyAllowedDays(policy: PropertyWaterPolicy | null): DayAbbr[] | null {
  return resolveRuleDays(resolvePropertyRule(policy), parseStreetNumber(policy?.address));
}

// ---------------------------------------------------------------------------
// Schedule violation detection (controller programmed schedule vs. the policy)
// ---------------------------------------------------------------------------

export type ViolationKind = 'day' | 'time' | 'max_days';

export interface Violation {
  kind: ViolationKind;
  program?: string; // program label (e.g. "A")
  /** machine-readable params for i18n; UI formats the message */
  days?: DayAbbr[];
  allowed?: DayAbbr[];
  window?: string;
  count?: number;
  max?: number;
}

export interface ProgramLike {
  /** The controller slot the program occupies: A / B / C / D. */
  program?: string;
  /** Human label for the slot ("Turf", "Shrubs/Color/Trees"), set in WaterScheduleManager. */
  programName?: string;
  /** New shape: a program (A/B/C/D) can have multiple start times. */
  startTimes?: string[];
  /** Legacy single start time — kept so older stored schedules still evaluate. */
  startTime?: string;
  duration?: number;
  days?: string[];
}

/**
 * A program's start times, tolerating the legacy single `startTime`. Always
 * returns an array so callers can iterate uniformly. Used by the violation
 * engine and the schedule renderers.
 */
export function readStartTimes(p: { startTimes?: string[]; startTime?: string }): string[] {
  if (p.startTimes && p.startTimes.length > 0) return p.startTimes.filter((t) => !!t);
  return p.startTime ? [p.startTime] : [];
}

/**
 * One human-readable line per program, e.g.
 *   `Turf: Mon, Fri @ 12:00 AM, 8:00 PM (382m)`
 *
 * The canonical rendering of a controller's programming. Previously this same
 * string was built in four places (the inspection report section, the XLSX and
 * PDF generators, and ad hoc in the report screens), which is how the Watering
 * Schedules report ended up collapsing every program into a bare union of days
 * instead. Callers that want the union still use unionProgrammedDays.
 *
 * Every field is treated as optional: stored schedules predate parts of this
 * shape, and a program missing its days or duration should still render what it
 * does have rather than vanish or throw.
 */
export function formatProgramLines(programs: ProgramLike[] | null | undefined, locale: string): string[] {
  if (!programs?.length) return [];
  return programs
    .map((p) => {
      const label = p.programName?.trim() || p.program?.trim() || '';
      const days = p.days?.length ? p.days.join(', ') : '';
      const times = readStartTimes(p)
        .map((st) => formatTimeOfDay(st, locale))
        .filter(Boolean)
        .join(', ');

      let line = days;
      if (times) line = line ? `${line} @ ${times}` : `@ ${times}`;
      if (typeof p.duration === 'number' && Number.isFinite(p.duration)) {
        line = line ? `${line} (${p.duration}m)` : `(${p.duration}m)`;
      }
      if (label) line = line ? `${label}: ${line}` : label;
      return line;
    })
    .filter(Boolean);
}

const toMin = (hhmm: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm ?? '');
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
};

/** True if a program running [start, start+duration] overlaps the no-water window. */
function overlapsWindow(startTime: string, duration: number, win: { start: string; end: string }): boolean {
  const s = toMin(startTime);
  const ws = toMin(win.start);
  const we = toMin(win.end);
  if (s == null || ws == null || we == null) return false;
  const e = s + (duration > 0 ? duration : 0);
  return s < we && e > ws;
}

/**
 * Compare a controller's programmed schedule against the property's rule.
 * Returns an empty array when there's no rule or no violations.
 */
export function findScheduleViolations(
  programs: ProgramLike[] | undefined,
  rule: WaterRule | null,
  streetNumber: number | null,
): Violation[] {
  if (!rule || !programs?.length) return [];
  const out: Violation[] = [];
  const allowed = resolveRuleDays(rule, streetNumber); // null = undeterminable; [] = none/no-restriction
  const restrictsDays = rule.assignment_type !== 'none' && allowed != null && allowed.length > 0;

  for (const p of programs) {
    const pDays = norm(p.days);
    if (restrictsDays) {
      const bad = pDays.filter((d) => !allowed!.includes(d));
      if (bad.length > 0) out.push({ kind: 'day', program: p.program, days: bad, allowed: allowed! });
    }
    if (rule.no_water_window && readStartTimes(p).some((st) => overlapsWindow(st, p.duration ?? 0, rule.no_water_window!))) {
      out.push({ kind: 'time', program: p.program, window: `${rule.no_water_window.start}–${rule.no_water_window.end}` });
    }
  }

  // Distinct days watered across all programs vs the weekly cap.
  if (rule.max_days_per_week != null) {
    const union = new Set<DayAbbr>();
    for (const p of programs) norm(p.days).forEach((d) => union.add(d));
    if (union.size > rule.max_days_per_week) {
      out.push({ kind: 'max_days', count: union.size, max: rule.max_days_per_week });
    }
  }
  return out;
}

/**
 * The distinct days a property is actually PROGRAMMED to water: the union across
 * every program on every controller, ordered Sun→Sat so two properties with the
 * same days render identically. Drives the "Programmed days" column of the
 * Watering Schedules report.
 *
 * Day strings go through the same `norm` the violation engine uses, so a
 * controller storing "Monday" or "MON" counts the same as "Mon". Matching the
 * raw strings instead would let this column read "not programmed" for a property
 * the engine had just judged non-compliant — a row contradicting itself.
 *
 * `hasAnyProgram` distinguishes "no program is defined at all" from "programs
 * exist but none name a day", which the report words differently.
 */
export function unionProgrammedDays(
  controllers: Array<{ water_start_times?: { programs?: ProgramLike[] } | null }>,
): { days: DayAbbr[]; hasAnyProgram: boolean } {
  const seen = new Set<DayAbbr>();
  let hasAnyProgram = false;
  for (const c of controllers) {
    for (const p of c.water_start_times?.programs ?? []) {
      hasAnyProgram = true;
      for (const d of norm(p.days)) seen.add(d);
    }
  }
  return { days: DAYS.filter((d) => seen.has(d)), hasAnyProgram };
}

// ---------------------------------------------------------------------------
// Per-property compliance (used by the city drill-down drawer)
// ---------------------------------------------------------------------------

// 'undetermined' = has a schedule + no day/time/frequency violations, but the
// city's DAY rule could not be applied (zone/trash-day cities stored as 'none',
// or a parity rule with no street number) — so day-compliance is unknown.
export type ComplianceStatus = 'compliant' | 'non_compliant' | 'undetermined' | 'no_schedule';

export interface ControllerScheduleLike {
  water_start_times?: { programs?: ProgramLike[] } | null;
  water_restriction_override?: { acknowledged?: boolean } | null;
}

export interface PropertyComplianceInput {
  address: string | null;
  water_restriction_class?: WaterClass | null;
  water_restriction_override?: WaterRule | null;
  controllers: ControllerScheduleLike[];
}

export interface ComplianceResult {
  status: ComplianceStatus;
  overridden: boolean;
  violationCount: number;
  /** violation counts by kind, summed across the property's controllers */
  kinds: Record<ViolationKind, number>;
  /** controller-level rollup */
  controllers: { total: number; withSchedule: number; compliant: number; violating: number };
}

/** Whether the city rule's DAY assignment can actually be checked for this property. */
function dayRuleEvaluable(rule: WaterRule, streetNumber: number | null): boolean {
  if (rule.assignment_type === 'fixed') return true;
  if (rule.assignment_type === 'parity' || rule.assignment_type === 'last_digit') {
    return resolveRuleDays(rule, streetNumber) != null;
  }
  return false; // 'none' → no day restriction defined, so day compliance is unknown
}

/**
 * Classify a property against a city schedule, with a per-kind violation tally
 * and a controller rollup. `no_schedule` = no city rule or no controller has a
 * programmed schedule. `undetermined` = clean on the checks we could run but the
 * day rule wasn't applicable. `overridden` = there ARE violations but every
 * violating controller was acknowledged (still non_compliant, just flagged).
 */
export function evaluatePropertyCompliance(
  schedule: WaterRestrictionSchedule | null,
  property: PropertyComplianceInput,
): ComplianceResult {
  const rule = resolvePropertyRule({
    city: null,
    zip: null,
    state: null,
    property_class: property.water_restriction_class ?? null,
    override: property.water_restriction_override ?? null,
    address: property.address,
    schedule,
  });
  const total = property.controllers.length;
  const withPrograms = property.controllers.filter(
    (c) => (c.water_start_times?.programs?.length ?? 0) > 0,
  );
  const kinds: Record<ViolationKind, number> = { day: 0, time: 0, max_days: 0 };
  const base = { kinds, controllers: { total, withSchedule: withPrograms.length, compliant: 0, violating: 0 } };

  if (!rule || withPrograms.length === 0) {
    return { status: 'no_schedule', overridden: false, violationCount: 0, ...base };
  }

  const streetNumber = parseStreetNumber(property.address);
  let violationCount = 0;
  let anyUnacknowledged = false;
  let compliant = 0;
  let violating = 0;
  for (const c of withPrograms) {
    const v = findScheduleViolations(c.water_start_times?.programs, rule, streetNumber);
    if (v.length > 0) {
      violating += 1;
      violationCount += v.length;
      for (const x of v) kinds[x.kind] += 1;
      if (!c.water_restriction_override?.acknowledged) anyUnacknowledged = true;
    } else {
      compliant += 1;
    }
  }
  const controllers = { total, withSchedule: withPrograms.length, compliant, violating };

  if (violationCount > 0) {
    return { status: 'non_compliant', overridden: !anyUnacknowledged, violationCount, kinds, controllers };
  }
  const status: ComplianceStatus = dayRuleEvaluable(rule, streetNumber) ? 'compliant' : 'undetermined';
  return { status, overridden: false, violationCount: 0, kinds, controllers };
}

// ---------------------------------------------------------------------------
// Detailed per-property compliance (used by the actionable Compliance Report).
// Same classification as evaluatePropertyCompliance, but also surfaces the exact
// violations per controller (which days, which program, the no-water window),
// the resolved rule + allowed days, so the report can tell a tech what to fix.
// ---------------------------------------------------------------------------

export interface ControllerComplianceDetail {
  controllerId: string;
  controllerLabel: string;
  /** the controller has a programmed schedule we could evaluate */
  hasSchedule: boolean;
  /** a violation was found but the property/tech acknowledged the override */
  acknowledged: boolean;
  violations: Violation[];
}

export interface DetailedComplianceInput {
  id: string;
  name: string;
  address: string | null;
  city?: string | null;
  water_restriction_class?: WaterClass | null;
  water_restriction_override?: WaterRule | null;
  controllers: Array<{
    id?: string;
    controller_number?: number | null;
    description?: string | null;
    location?: string | null;
    water_start_times?: { programs?: ProgramLike[] } | null;
    water_restriction_override?: { acknowledged?: boolean } | null;
  }>;
}

export interface DetailedComplianceResult extends ComplianceResult {
  rule: WaterRule | null;
  allowedDays: DayAbbr[] | null;
  controllerDetails: ControllerComplianceDetail[];
}

function controllerLabel(c: DetailedComplianceInput['controllers'][number]): string {
  return (
    (c.description && c.description.trim()) ||
    (c.location && c.location.trim()) ||
    (c.controller_number != null ? `Controller ${c.controller_number}` : 'Controller')
  );
}

export function evaluatePropertyComplianceDetailed(
  schedule: WaterRestrictionSchedule | null,
  property: DetailedComplianceInput,
): DetailedComplianceResult {
  const base = evaluatePropertyCompliance(schedule, {
    address: property.address,
    water_restriction_class: property.water_restriction_class ?? null,
    water_restriction_override: property.water_restriction_override ?? null,
    controllers: property.controllers,
  });
  const rule = resolvePropertyRule({
    city: property.city ?? null,
    zip: null,
    state: null,
    property_class: property.water_restriction_class ?? null,
    override: property.water_restriction_override ?? null,
    address: property.address,
    schedule,
  });
  const streetNumber = parseStreetNumber(property.address);
  const allowedDays = rule ? resolveRuleDays(rule, streetNumber) : null;

  const controllerDetails: ControllerComplianceDetail[] = property.controllers
    .filter((c) => (c.water_start_times?.programs?.length ?? 0) > 0)
    .map((c) => {
      const violations = rule ? findScheduleViolations(c.water_start_times?.programs, rule, streetNumber) : [];
      return {
        controllerId: c.id ?? '',
        controllerLabel: controllerLabel(c),
        hasSchedule: true,
        acknowledged: Boolean(c.water_restriction_override?.acknowledged),
        violations,
      };
    });

  return { ...base, rule, allowedDays, controllerDetails };
}
